import React, { useState, useEffect } from 'react';
import { ChevronDown, ChevronUp, Heart, Sun, Coffee, Users, Activity, Smile, Leaf, Clock, Compass, Dumbbell, Zap, Share2, Lightbulb, Save, History, RotateCcw, Flower } from 'lucide-react';
import { useUser } from '../context/UserContext';
import { db } from '../firebase';
import { collection, addDoc, query, orderBy, getDocs } from 'firebase/firestore';
import { IkigaiPillars, IkigaiHistoryEntry } from '../types';

interface IkigaiRule {
  id: number;
  title: string;
  japanese: string;
  description: string;
  icon: string;
}

const IKIGAI_RULES: IkigaiRule[] = [
  {
    id: 1,
    title: "Stay Active; Don't Retire",
    japanese: "生涯現役",
    description: "Those who give up the things they love doing and do well lose their purpose in life. Keep doing things of value, making progress, bringing beauty or utility to others, helping out, and shaping the world around you, even after your 'official' professional activity has ended.",
    icon: "Activity"
  },
  {
    id: 2,
    title: "Take It Slow",
    japanese: "慢",
    description: "Being in a hurry is inversely proportional to quality of life. As the old saying goes, 'Walk slowly and you'll go far.' When we leave urgency behind, life and time take on new meaning.",
    icon: "Clock"
  },
  {
    id: 3,
    title: "Don't Fill Your Stomach",
    japanese: "腹八分目",
    description: "Less is more when it comes to eating for long life, too. According to the 80 percent rule, in order to stay healthier longer, we should eat a little less than our hunger demands instead of stuffing ourselves.",
    icon: "Coffee"
  },
  {
    id: 4,
    title: "Surround Yourself with Good Friends",
    japanese: "友",
    description: "Friends are the best medicine. There for confiding worries, sharing stories that brighten your day, getting advice, having fun, dreaming... in other words, living.",
    icon: "Users"
  },
  {
    id: 5,
    title: "Get in Shape for Your Next Birthday",
    japanese: "建",
    description: "Water moves; it is at its best when it flows fresh and doesn't stagnate. The body you move through life in needs a bit of daily maintenance to keep it running for a long time.",
    icon: "Dumbbell"
  },
  {
    id: 6,
    title: "Smile",
    japanese: "笑",
    description: "A cheerful attitude is not only relaxing—it also helps make friends. It's good to recognize the things that aren't so great, but we should never forget what a privilege it is to be in the here and now.",
    icon: "Smile"
  },
  {
    id: 7,
    title: "Reconnect with Nature",
    japanese: "自然",
    description: "Though most people live in cities these days, human beings are made to be part of the natural world. We should return to it often to recharge our batteries.",
    icon: "Leaf"
  },
  {
    id: 8,
    title: "Give Thanks",
    japanese: "感謝",
    description: "To your ancestors, to nature, which provides you with the air you breathe and the food you eat, to your friends and family, to everything that brightens your days and makes you feel lucky to be alive.",
    icon: "Heart"
  },
  {
    id: 9,
    title: "Live in the Moment",
    japanese: "今",
    description: "Stop regretting the past and fearing the future. Today is all you have. Make the most of it. Make it worth remembering.",
    icon: "Zap"
  },
  {
    id: 10,
    title: "Follow Your Ikigai",
    japanese: "生き甲斐",
    description: "There is a passion inside you, a unique talent that gives meaning to your days and drives you to share the best of yourself until the very end. If you don't know what your ikigai is yet, as Viktor Frankl says, your mission is to discover it.",
    icon: "Compass"
  }
];

const IconMap: Record<string, React.ReactNode> = {
  Activity: <Activity size={24} />,
  Clock: <Clock size={24} />,
  Coffee: <Coffee size={24} />,
  Users: <Users size={24} />,
  Dumbbell: <Dumbbell size={24} />,
  Smile: <Smile size={24} />,
  Leaf: <Leaf size={24} />,
  Heart: <Heart size={24} />,
  Zap: <Zap size={24} />,
  Compass: <Compass size={24} />,
};

// Helper Icons
const StarIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"></polygon></svg>
);
const CoinsIcon = () => (
    <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="8" cy="8" r="6"/><path d="M18.09 10.37A6 6 0 1 1 10.34 18"/><path d="M7 6h1v4"/><path d="m16.71 13.88.7 .71-2.82 2.82"/></svg>
);

const IkigaiDiagram: React.FC<{ pillars: IkigaiPillars }> = ({ pillars }) => {
  const hasLove = pillars.love.length > 5;
  const hasGood = pillars.goodAt.length > 5;
  const hasNeed = pillars.worldNeeds.length > 5;
  const hasPaid = pillars.paidFor.length > 5;

  const isPassion = hasLove && hasGood;
  const isMission = hasLove && hasNeed;
  const isProfession = hasGood && hasPaid;
  const isVocation = hasNeed && hasPaid;
  const isIkigai = hasLove && hasGood && hasNeed && hasPaid;

  const getOpacity = (active: boolean) => active ? 0.8 : 0.1;
  const getColor = (active: boolean, color: string) => active ? color : '#e2e8f0';

  return (
    <div className="flex justify-center my-12 animate-fadeIn">
      <div className="relative w-[300px] h-[300px] sm:w-[400px] sm:h-[400px]">
        <svg viewBox="0 0 400 400" className="w-full h-full drop-shadow-xl">
          <defs>
            <filter id="glow">
              <feGaussianBlur stdDeviation="2.5" result="coloredBlur"/>
              <feMerge>
                <feMergeNode in="coloredBlur"/>
                <feMergeNode in="SourceGraphic"/>
              </feMerge>
            </filter>
          </defs>

          {/* Top Circle - LOVE */}
          <circle cx="200" cy="140" r="100" fill={getColor(hasLove, '#fca5a5')} fillOpacity="0.4" stroke={hasLove ? '#e11d48' : '#94a3b8'} strokeWidth="2" className="transition-all duration-700" />
          <text x="200" y="80" textAnchor="middle" className="text-xs font-bold fill-rose-700 uppercase tracking-widest">Love</text>

          {/* Left Circle - GOOD AT */}
          <circle cx="140" cy="200" r="100" fill={getColor(hasGood, '#93c5fd')} fillOpacity="0.4" stroke={hasGood ? '#2563eb' : '#94a3b8'} strokeWidth="2" className="transition-all duration-700" />
          <text x="80" y="200" textAnchor="middle" className="text-xs font-bold fill-blue-700 uppercase tracking-widest rotate-[-90deg] translate-[-10, 0]">Good At</text>

          {/* Right Circle - WORLD NEEDS */}
          <circle cx="260" cy="200" r="100" fill={getColor(hasNeed, '#fcd34d')} fillOpacity="0.4" stroke={hasNeed ? '#d97706' : '#94a3b8'} strokeWidth="2" className="transition-all duration-700" />
          <text x="320" y="200" textAnchor="middle" className="text-xs font-bold fill-amber-700 uppercase tracking-widest rotate-[90deg]">Needs</text>

          {/* Bottom Circle - PAID FOR */}
          <circle cx="200" cy="260" r="100" fill={getColor(hasPaid, '#6ee7b7')} fillOpacity="0.4" stroke={hasPaid ? '#059669' : '#94a3b8'} strokeWidth="2" className="transition-all duration-700" />
          <text x="200" y="330" textAnchor="middle" className="text-xs font-bold fill-emerald-700 uppercase tracking-widest">Paid</text>

          {/* Intersections Labels */}
          {isPassion && <text x="160" y="150" textAnchor="middle" className="text-[10px] sm:text-xs font-bold fill-purple-800 animate-pulse">PASSION</text>}
          {isMission && <text x="240" y="150" textAnchor="middle" className="text-[10px] sm:text-xs font-bold fill-orange-800 animate-pulse">MISSION</text>}
          {isProfession && <text x="160" y="250" textAnchor="middle" className="text-[10px] sm:text-xs font-bold fill-cyan-800 animate-pulse">PROFESSION</text>}
          {isVocation && <text x="240" y="250" textAnchor="middle" className="text-[10px] sm:text-xs font-bold fill-lime-800 animate-pulse">VOCATION</text>}

          {/* Center Ikigai */}
          <circle cx="200" cy="200" r={isIkigai ? "30" : "20"} fill={isIkigai ? '#ec4899' : 'white'} fillOpacity={isIkigai ? 1 : 0.5} className="transition-all duration-1000" filter={isIkigai ? "url(#glow)" : ""} />
          <text x="200" y="205" textAnchor="middle" className={`font-serif font-bold transition-all duration-700 ${isIkigai ? 'fill-white text-lg' : 'fill-slate-400 text-xs'}`}>
            IKIGAI
          </text>
        </svg>
      </div>
    </div>
  );
};

const Ikigai: React.FC = () => {
  const { user } = useUser();
  const [openId, setOpenId] = useState<number | null>(1);
  const [pillars, setPillars] = useState<IkigaiPillars>({
    love: "",
    goodAt: "",
    worldNeeds: "",
    paidFor: ""
  });
  const [activePrompt, setActivePrompt] = useState<string | null>(null);
  const [history, setHistory] = useState<IkigaiHistoryEntry[]>([]);
  const [loadingHistory, setLoadingHistory] = useState(true);
  const [saving, setSaving] = useState(false);
  const [showHistory, setShowHistory] = useState(false);

  // Load local draft and remote history on mount
  useEffect(() => {
    // 1. Local Draft
    const saved = localStorage.getItem('ikigai_pillars');
    if (saved) {
      setPillars(JSON.parse(saved));
    }

    // 2. Fetch History if User Logged In
    const fetchHistory = async () => {
        if (!user.uid) {
            setLoadingHistory(false);
            return;
        }

        try {
            const historyRef = collection(db, 'users', user.uid, 'ikigai_history');
            const q = query(historyRef, orderBy('timestamp', 'desc'));
            const querySnapshot = await getDocs(q);
            const loadedHistory: IkigaiHistoryEntry[] = [];
            querySnapshot.forEach((doc) => {
                const data = doc.data();
                loadedHistory.push({
                    id: doc.id,
                    timestamp: data.timestamp,
                    pillars: data.pillars as IkigaiPillars
                });
            });
            setHistory(loadedHistory);
        } catch (e) {
            console.error("Error fetching Ikigai history:", e);
        } finally {
            setLoadingHistory(false);
        }
    };

    fetchHistory();
  }, [user.uid]);

  // Sync to LocalStorage (Working Draft)
  useEffect(() => {
    localStorage.setItem('ikigai_pillars', JSON.stringify(pillars));
  }, [pillars]);

  const toggle = (id: number) => {
    setOpenId(openId === id ? null : id);
  };

  const handleInput = (key: keyof IkigaiPillars, value: string) => {
    setPillars(prev => ({ ...prev, [key]: value }));
  };

  const handleSaveSnapshot = async () => {
      if (!user.uid) {
          alert("Please sign in to save your history.");
          return;
      }
      setSaving(true);
      try {
          const newEntry = {
              pillars: pillars,
              timestamp: new Date().toISOString()
          };
          const docRef = await addDoc(collection(db, 'users', user.uid, 'ikigai_history'), newEntry);
          
          setHistory(prev => [{
              id: docRef.id,
              ...newEntry
          }, ...prev]);
          
          alert("Snapshot saved to your profile!");
      } catch (e) {
          console.error("Error saving snapshot", e);
          alert("Failed to save snapshot.");
      } finally {
          setSaving(false);
      }
  };

  const handleLoadSnapshot = (entry: IkigaiHistoryEntry) => {
      if (confirm("Load this version? This will overwrite your current inputs.")) {
          setPillars(entry.pillars);
          setShowHistory(false);
      }
  };

  const handleShare = async () => {
    const text = `My Ikigai Journey\n\n❤️ I Love: ${pillars.love || '...'}\n✨ I am Good At: ${pillars.goodAt || '...'}\n🌍 The World Needs: ${pillars.worldNeeds || '...'}\n💰 I can be Paid For: ${pillars.paidFor || '...'}\n\nBuilt with Nunchi Master App`;
    
    if (navigator.share) {
      try {
        await navigator.share({
          title: 'My Ikigai',
          text: text,
        });
      } catch (err) {
        console.error("Share failed", err);
      }
    } else {
      navigator.clipboard.writeText(text);
      alert("Summary copied to clipboard!");
    }
  };

  const PROMPTS = {
    love: [
      "What do you do in your free time that makes you lose track of time?",
      "What topics do you find yourself constantly reading or learning about?",
      "What would you do if money were no object?"
    ],
    goodAt: [
      "What do people ask you for help with?",
      "What skills have you received compliments on?",
      "What tasks come easily to you that others find difficult?"
    ],
    worldNeeds: [
      "What problems in your community or the world touch your heart?",
      "What change do you wish to see in the world?",
      "Who are the people you most want to serve?"
    ],
    paidFor: [
      "What skills do you have that are currently in demand?",
      "What have you been paid for in the past?",
      "What service or product could you create that provides value?"
    ]
  };

  return (
    <div className="max-w-4xl mx-auto px-6 pb-20 animate-fadeIn">
      <header className="mb-10 text-center">
        <div className="inline-block p-3 bg-pink-50 text-pink-500 rounded-full mb-4">
          <Flower size={32} />
        </div>
        <h1 className="text-4xl font-serif font-bold text-nunchi-900 mb-3">Ikigai</h1>
        <p className="text-xl text-nunchi-500 italic font-serif mb-6">"A Reason for Being"</p>
        <p className="text-nunchi-600 max-w-xl mx-auto leading-relaxed">
          While Nunchi helps you navigate the outer world of social harmony, 
          Ikigai helps you navigate your inner world to find purpose and joy.
        </p>
      </header>

      {/* Interactive Builder */}
      <section className="mb-8">
        <div className="flex flex-col sm:flex-row justify-between items-center mb-6 gap-4">
          <h2 className="text-2xl font-bold text-nunchi-800">Map Your Ikigai</h2>
          <div className="flex items-center gap-2">
            <button 
                onClick={() => setShowHistory(!showHistory)}
                className="flex items-center gap-2 text-sm font-bold text-nunchi-600 bg-white border border-nunchi-200 px-4 py-2 rounded-full hover:bg-nunchi-50 transition-colors"
            >
                <History size={16} /> {showHistory ? 'Hide History' : 'History'}
            </button>
            <button 
                onClick={handleSaveSnapshot}
                disabled={saving}
                className="flex items-center gap-2 text-sm font-bold text-white bg-nunchi-700 border border-transparent px-4 py-2 rounded-full hover:bg-nunchi-800 transition-colors disabled:opacity-50"
            >
                {saving ? <RotateCcw className="animate-spin" size={16} /> : <Save size={16} />} Save
            </button>
            <button 
              onClick={handleShare}
              className="flex items-center gap-2 text-sm font-bold text-nunchi-600 bg-white border border-nunchi-200 px-4 py-2 rounded-full hover:bg-nunchi-50 transition-colors"
            >
              <Share2 size={16} /> Share
            </button>
          </div>
        </div>

        {/* History Panel */}
        {showHistory && (
            <div className="mb-8 bg-nunchi-50 border border-nunchi-200 rounded-2xl p-6 animate-fadeIn">
                <h3 className="font-bold text-nunchi-800 mb-4 flex items-center gap-2">
                    <History size={18} /> Previous Versions
                </h3>
                {loadingHistory ? (
                    <div className="text-center py-4 text-nunchi-500 text-sm">Loading history...</div>
                ) : history.length === 0 ? (
                    <div className="text-center py-4 text-nunchi-500 text-sm">No saved snapshots yet.</div>
                ) : (
                    <div className="space-y-3 max-h-60 overflow-y-auto pr-2">
                        {history.map((entry) => (
                            <div key={entry.id} className="bg-white p-4 rounded-xl border border-nunchi-100 flex justify-between items-center hover:shadow-sm transition-shadow">
                                <div>
                                    <div className="text-sm font-bold text-nunchi-800">
                                        {new Date(entry.timestamp).toLocaleDateString()}
                                    </div>
                                    <div className="text-xs text-nunchi-400">
                                        {new Date(entry.timestamp).toLocaleTimeString()}
                                    </div>
                                </div>
                                <button 
                                    onClick={() => handleLoadSnapshot(entry)}
                                    className="text-xs font-bold bg-nunchi-100 text-nunchi-600 px-3 py-1.5 rounded-lg hover:bg-nunchi-200"
                                >
                                    Load
                                </button>
                            </div>
                        ))}
                    </div>
                )}
            </div>
        )}

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 relative">
          
          {/* Love */}
          <div className="bg-rose-50/50 p-6 rounded-3xl border-2 border-rose-100 relative group">
            <div className="flex justify-between items-start mb-2">
              <label className="font-bold text-rose-700 flex items-center gap-2">
                <Heart size={18} /> What you LOVE
              </label>
              <button 
                onClick={() => setActivePrompt(activePrompt === 'love' ? null : 'love')}
                className="text-rose-400 hover:text-rose-600"
              >
                <Lightbulb size={18} />
              </button>
            </div>
            {activePrompt === 'love' && (
              <div className="mb-3 text-xs text-rose-600 bg-rose-100 p-3 rounded-lg animate-fadeIn">
                <p className="font-bold mb-1">Ask yourself:</p>
                <ul className="list-disc pl-4 space-y-1">
                  {PROMPTS.love.map((p, i) => <li key={i}>{p}</li>)}
                </ul>
              </div>
            )}
            <textarea 
              value={pillars.love}
              onChange={(e) => handleInput('love', e.target.value)}
              placeholder="Painting, Coding, Teaching..."
              className="w-full h-32 bg-white rounded-xl p-4 border border-rose-100 focus:border-rose-300 focus:ring-2 focus:ring-rose-100 outline-none resize-none text-nunchi-700 placeholder:text-rose-200"
            />
          </div>

          {/* World Needs */}
          <div className="bg-amber-50/50 p-6 rounded-3xl border-2 border-amber-100 relative">
            <div className="flex justify-between items-start mb-2">
              <label className="font-bold text-amber-700 flex items-center gap-2">
                <Sun size={18} /> What the world NEEDS
              </label>
              <button 
                onClick={() => setActivePrompt(activePrompt === 'worldNeeds' ? null : 'worldNeeds')}
                className="text-amber-400 hover:text-amber-600"
              >
                <Lightbulb size={18} />
              </button>
            </div>
            {activePrompt === 'worldNeeds' && (
              <div className="mb-3 text-xs text-amber-800 bg-amber-100 p-3 rounded-lg animate-fadeIn">
                <p className="font-bold mb-1">Ask yourself:</p>
                <ul className="list-disc pl-4 space-y-1">
                  {PROMPTS.worldNeeds.map((p, i) => <li key={i}>{p}</li>)}
                </ul>
              </div>
            )}
            <textarea 
              value={pillars.worldNeeds}
              onChange={(e) => handleInput('worldNeeds', e.target.value)}
              placeholder="Clean water, Empathy, Better software..."
              className="w-full h-32 bg-white rounded-xl p-4 border border-amber-100 focus:border-amber-300 focus:ring-2 focus:ring-amber-100 outline-none resize-none text-nunchi-700 placeholder:text-amber-200"
            />
          </div>

          {/* Good At */}
          <div className="bg-blue-50/50 p-6 rounded-3xl border-2 border-blue-100 relative">
            <div className="flex justify-between items-start mb-2">
              <label className="font-bold text-blue-700 flex items-center gap-2">
                <StarIcon /> What you are GOOD AT
              </label>
              <button 
                onClick={() => setActivePrompt(activePrompt === 'goodAt' ? null : 'goodAt')}
                className="text-blue-400 hover:text-blue-600"
              >
                <Lightbulb size={18} />
              </button>
            </div>
            {activePrompt === 'goodAt' && (
              <div className="mb-3 text-xs text-blue-800 bg-blue-100 p-3 rounded-lg animate-fadeIn">
                 <p className="font-bold mb-1">Ask yourself:</p>
                <ul className="list-disc pl-4 space-y-1">
                  {PROMPTS.goodAt.map((p, i) => <li key={i}>{p}</li>)}
                </ul>
              </div>
            )}
            <textarea 
              value={pillars.goodAt}
              onChange={(e) => handleInput('goodAt', e.target.value)}
              placeholder="Listening, Analysis, Design..."
              className="w-full h-32 bg-white rounded-xl p-4 border border-blue-100 focus:border-blue-300 focus:ring-2 focus:ring-blue-100 outline-none resize-none text-nunchi-700 placeholder:text-blue-200"
            />
          </div>

          {/* Paid For */}
          <div className="bg-emerald-50/50 p-6 rounded-3xl border-2 border-emerald-100 relative">
            <div className="flex justify-between items-start mb-2">
              <label className="font-bold text-emerald-700 flex items-center gap-2">
                <CoinsIcon /> What you can be PAID FOR
              </label>
              <button 
                onClick={() => setActivePrompt(activePrompt === 'paidFor' ? null : 'paidFor')}
                className="text-emerald-400 hover:text-emerald-600"
              >
                <Lightbulb size={18} />
              </button>
            </div>
            {activePrompt === 'paidFor' && (
              <div className="mb-3 text-xs text-emerald-800 bg-emerald-100 p-3 rounded-lg animate-fadeIn">
                 <p className="font-bold mb-1">Ask yourself:</p>
                <ul className="list-disc pl-4 space-y-1">
                  {PROMPTS.paidFor.map((p, i) => <li key={i}>{p}</li>)}
                </ul>
              </div>
            )}
            <textarea 
              value={pillars.paidFor}
              onChange={(e) => handleInput('paidFor', e.target.value)}
              placeholder="Consulting, Developing, Coaching..."
              className="w-full h-32 bg-white rounded-xl p-4 border border-emerald-100 focus:border-emerald-300 focus:ring-2 focus:ring-emerald-100 outline-none resize-none text-nunchi-700 placeholder:text-emerald-200"
            />
          </div>
        </div>
      </section>

      {/* Diagram Section */}
      <section className="bg-nunchi-50 rounded-3xl p-6 mb-16 border border-nunchi-100 shadow-inner">
         <h3 className="text-center font-bold text-nunchi-700 uppercase tracking-widest text-sm mb-2">Your Ikigai Map</h3>
         <IkigaiDiagram pillars={pillars} />
         <p className="text-center text-xs text-nunchi-400 italic">Fill out all sections to reveal the center.</p>
      </section>

      {/* The 10 Rules */}
      <section>
        <h2 className="text-2xl font-bold text-nunchi-800 mb-6 text-center">The 10 Rules of Ikigai</h2>
        <div className="space-y-4">
          {IKIGAI_RULES.map((rule) => (
            <div 
              key={rule.id} 
              className={`bg-white border rounded-xl overflow-hidden transition-all duration-300 ${openId === rule.id ? 'border-pink-300 shadow-md' : 'border-nunchi-200 hover:border-pink-200'}`}
            >
              <button 
                onClick={() => toggle(rule.id)}
                className="w-full flex items-center justify-between p-5 text-left focus:outline-none"
              >
                <div className="flex items-center gap-4">
                  <div className={`p-2 rounded-lg ${openId === rule.id ? 'bg-pink-100 text-pink-600' : 'bg-gray-50 text-gray-400'}`}>
                    {IconMap[rule.icon]}
                  </div>
                  <div>
                    <h3 className={`font-bold text-lg ${openId === rule.id ? 'text-nunchi-900' : 'text-gray-700'}`}>
                      {rule.title}
                    </h3>
                    <p className="text-xs text-nunchi-500 font-serif">{rule.japanese}</p>
                  </div>
                </div>
                <div className="text-nunchi-400">
                  {openId === rule.id ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
                </div>
              </button>
              
              <div className={`overflow-hidden transition-[max-height] duration-500 ease-in-out ${openId === rule.id ? 'max-h-40' : 'max-h-0'}`}>
                <div className="p-5 pt-0 text-nunchi-600 leading-relaxed border-t border-dashed border-pink-100 mt-2">
                  {rule.description}
                </div>
              </div>
            </div>
          ))}
        </div>
      </section>
    </div>
  );
};

export default Ikigai;